// Tenantra Inc. - Admin Contact Us section: leads submitted from the public contact form.
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Mail, Trash2, Inbox, CheckCircle2, Phone } from 'lucide-react';
import api, { apiHelpers } from '@/services/api';
import { formatDate } from '@/utils/date';

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-4.5 h-4.5" /></div>
      <div>
        <div className="text-xl font-bold text-slate-900 leading-none">{value}</div>
        <div className="text-[11px] text-slate-500 mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function ContactsPanel() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ new: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [rowBusy, setRowBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/contacts');
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (e) { if (e?.response?.status === 403) setDenied(true); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setBusy = (id, v) => setRowBusy((b) => ({ ...b, [id]: v }));

  const markRead = async (id) => {
    setBusy(id, true);
    try { await api.post(`/admin/contacts/${id}/read`); load(); }
    catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(id, false); }
  };
  const remove = async (id, name) => {
    if (!window.confirm(`Delete the submission from ${name}?`)) return;
    setBusy(id, true);
    try { await api.delete(`/admin/contacts/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(id, false); }
  };

  if (denied) return <div className="p-6"><p className="text-slate-600">You don't have access to the Contact Us section.</p></div>;

  return (
    <div className="space-y-6" data-testid="contacts-panel">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Contact Us</h1>
        <p className="text-sm text-slate-500">Leads submitted from your public contact form. Share your <span className="font-medium text-slate-700">/contact</span> link in ads to collect them.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Stat icon={Inbox} label="Total leads" value={counts.total || 0} color="bg-slate-100 text-slate-600" />
        <Stat icon={Mail} label="New" value={counts.new || 0} color="bg-teal-100 text-teal-600" />
        <Stat icon={CheckCircle2} label="Reviewed" value={(counts.total || 0) - (counts.new || 0)} color="bg-emerald-100 text-emerald-600" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No contact submissions yet. Share your <span className="font-medium text-slate-700">/contact</span> link in your ads to start collecting leads.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-2.5 font-semibold">Name</th>
                  <th className="px-4 py-2.5 font-semibold">Email</th>
                  <th className="px-4 py-2.5 font-semibold">Phone</th>
                  <th className="px-4 py-2.5 font-semibold hidden md:table-cell">Received</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((c) => (
                  <tr key={c.id} data-testid={`contact-row-${c.id}`} className={c.status === 'new' ? 'bg-teal-50/30' : ''}>
                    <td className="px-4 py-3 text-slate-800 font-medium whitespace-nowrap">
                      {c.full_name}
                      {c.message && <div className="text-[11px] text-slate-400 font-normal max-w-xs truncate">{c.message}</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-600"><a href={`mailto:${c.email}`} className="hover:text-teal-700">{c.email}</a></td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap"><a href={`tel:${c.phone}`} className="hover:text-teal-700 inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</a></td>
                    <td className="px-4 py-3 text-slate-500 hidden md:table-cell whitespace-nowrap">{formatDate(c.created_at)}</td>
                    <td className="px-4 py-3"><span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${c.status === 'new' ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>{c.status}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {c.status === 'new' && (
                          <button disabled={rowBusy[c.id]} onClick={() => markRead(c.id)} className="h-8 px-2.5 rounded-md border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1" data-testid={`contact-read-${c.id}`}>
                            {rowBusy[c.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}Mark read
                          </button>
                        )}
                        <button disabled={rowBusy[c.id]} onClick={() => remove(c.id, c.full_name)} className="h-8 px-2.5 rounded-md border border-red-200 text-xs text-red-600 hover:bg-red-50 inline-flex items-center gap-1" data-testid={`contact-remove-${c.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
